import type { AIModelChoices, Annotation } from 'api';
import { addError } from 'monitor';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { getIgnoredLabels, getLabelsWithReducedDarkening } from '../../constants';
import { type MobileZoom, renderCanvas } from '../../utils/canvas/canvas';
import { getGroupedAnnotations } from '../../utils/formatAnnotations/formatAnnotations';

const IMAGE_CACHE_MAX_SIZE = 100;

class LRUImageCache {
  private cache: Map<string, HTMLImageElement>;
  private readonly maxSize: number;

  constructor(maxSize: number) {
    this.cache = new Map();
    this.maxSize = maxSize;
  }

  get(src: string): HTMLImageElement | undefined {
    const image = this.cache.get(src);
    if (!image) return undefined;

    // Promote to most recently used
    this.cache.delete(src);
    this.cache.set(src, image);
    return image;
  }

  set(src: string, image: HTMLImageElement): void {
    if (this.cache.has(src)) {
      this.cache.delete(src);
    }
    this.cache.set(src, image);

    if (this.cache.size > this.maxSize) {
      const oldestEntry = this.cache.keys().next();
      if (!oldestEntry.done) {
        this.cache.delete(oldestEntry.value);
      }
    }
  }

  delete(src: string): void {
    this.cache.delete(src);
  }
}

const GLOBAL_IMAGE_CACHE = new LRUImageCache(IMAGE_CACHE_MAX_SIZE);

const getOrCreateImage = (src: string): HTMLImageElement => {
  const cached = GLOBAL_IMAGE_CACHE.get(src);
  if (cached) {
    return cached;
  }

  const image = new Image();
  image.crossOrigin = 'anonymous';
  if ('decoding' in image) {
    image.decoding = 'async';
  }
  image.src = src;
  GLOBAL_IMAGE_CACHE.set(src, image);
  return image;
};

const removeCachedImage = (src: string, image: HTMLImageElement) => {
  const cached = GLOBAL_IMAGE_CACHE.get(src);
  if (cached === image) {
    GLOBAL_IMAGE_CACHE.delete(src);
  }
};

interface UseCanvasRenderingProps {
  imageSrc: string;
  annotations: Annotation[];
  hiddenCategories: readonly string[];
  model: AIModelChoices;
  showLegend: boolean;
  showAerosols: boolean;
  isMobile: boolean;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  mobileZoom: MobileZoom;
}

interface UseCanvasRenderingReturn {
  isLoading: boolean;
  visibleAnnotations: Annotation[];
  labelColourMap: Map<string, readonly number[]>;
  scale: number | null;
}

export const useCanvasRendering = ({
  imageSrc,
  annotations,
  hiddenCategories,
  model,
  showLegend,
  isMobile,
  canvasRef,
  mobileZoom,
  showAerosols,
}: UseCanvasRenderingProps): UseCanvasRenderingReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [scale, setScale] = useState<number | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const visibleAnnotations = useMemo(() => {
    if (!annotations) return [];
    const ignoredLabels = getIgnoredLabels(model);
    return annotations.filter(({ label }) => !ignoredLabels.includes(label) && !hiddenCategories.includes(label));
  }, [annotations, hiddenCategories, model]);

  const excludedFromDarkeningLabels = useMemo(() => {
    if (!showLegend) {
      return undefined;
    }
    return new Set(getLabelsWithReducedDarkening({ model, showAerosols }));
  }, [model, showAerosols, showLegend]);

  const shouldDarken = useMemo(() => {
    return showLegend && visibleAnnotations.length > 0;
  }, [showLegend, visibleAnnotations]);

  const labelColourMap = useMemo(() => {
    const groupedAnnotations = getGroupedAnnotations(annotations, model, showAerosols);
    const colourMap = new Map<string, readonly number[]>();

    for (const group of groupedAnnotations) {
      for (const label of group.originalLabels) {
        colourMap.set(label, group.colour);
      }
    }

    return colourMap;
  }, [annotations, model, showAerosols]);

  const handleErrorRef = useRef(() => {
    setIsLoading(false);
    addError(Error('Failed to render canvas'));
  });

  const handleLoadStartRef = useRef(() => {
    setIsLoading(true);
  });

  const handleLoadEndRef = useRef(() => {
    setIsLoading(false);
  });

  // eslint-disable-next-line react-hooks/refs
  handleErrorRef.current = () => {
    setIsLoading(false);
    addError(Error('Failed to render canvas'));
  };

  // eslint-disable-next-line react-hooks/refs
  handleLoadStartRef.current = () => {
    setIsLoading(true);
  };

  // eslint-disable-next-line react-hooks/refs
  handleLoadEndRef.current = () => {
    setIsLoading(false);
  };

  const handleScaleCalculatedRef = useRef((calculatedScale: number) => {
    setScale(calculatedScale);
  });

  // eslint-disable-next-line react-hooks/refs
  handleScaleCalculatedRef.current = (calculatedScale: number) => {
    setScale(calculatedScale);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const image = getOrCreateImage(imageSrc);
    imageRef.current = image;

    const handleError = () => {
      removeCachedImage(imageSrc, image);
      if (imageRef.current === image) {
        imageRef.current = null;
      }
      handleErrorRef.current();
    };

    const cleanup = renderCanvas({
      annotations: visibleAnnotations,
      canvas,
      ctx,
      excludedFromDarkeningLabels,
      hiddenCategories,
      image,
      isMobile,
      labelColourMap,
      onError: handleError,
      onLoadEnd: handleLoadEndRef.current,
      onLoadStart: handleLoadStartRef.current,
      onScaleCalculated: handleScaleCalculatedRef.current,
      shouldDarken,
      zoom: mobileZoom,
    });

    return () => {
      cleanup?.();
      if (imageRef.current === image) {
        imageRef.current = null;
      }
    };
  }, [
    imageSrc,
    canvasRef,
    visibleAnnotations,
    hiddenCategories,
    labelColourMap,
    excludedFromDarkeningLabels,
    isMobile,
    shouldDarken,
    mobileZoom,
  ]);

  return {
    isLoading,
    labelColourMap,
    scale,
    visibleAnnotations,
  };
};
